// src/features/leases/deriveLeaseActions.js
//
// LEASES-ACTION-VIEW S1 — Pure, memoizable selector that enriches the
// filtered lease array with action-window metadata for the Action view.
//
// deriveLeaseActions(leases, criticalDates, today, focusDays)
//   → enriched lease objects with:
//       next_action_date     — earliest future decision-point date (ISO)
//       next_action_type     — type string of the governing critical date
//       days_to_next_action  — signed integer days (null = no date)
//       action_window_bucket — "decide-now" | "negotiate" | "reduce-exposure" | "monitor" | "expired"
//       action_value         — annual_cost in the lease's native currency
//       action_short_reason  — short label explaining the bucket assignment
//       isStrategic          — true when a hub+plan-linked lease is elevated
//
// Bucket rules (before Focus override):
//   days ≤ 90          → decide-now
//   91 ≤ days ≤ 365    → negotiate
//   days > 365 | null  → monitor
//
// Focus override: any lease whose days_to_next_action exceeds the Focus
// window (focusDays) is forced to "monitor" regardless of its raw bucket.
//
//
// Date source: getUpcomingMarkers() per lease (re-uses upcomingMarkers.js
// so the same set of critical dates drives both surfaces).
// Fallback when no action-date marker exists: resolveTimelineEndAnchor().
//
// All date parsing goes through parseDateOnly (L63).

import {
  isSentinelEndDate,
  parseDateOnly,
  resolveTimelineEndAnchor,
} from "./leaseTabConfig.js";
import { getUpcomingMarkers } from "./upcomingMarkers.js";
import {
  CURRENT_TERM_KEY_DATE_TYPES,
  getCurrentTermStartDate,
  isVerifiedLeaseDecisionDate,
  selectCurrentTermNoticeWindow,
  selectCurrentTermKeyDates,
} from "./leaseDetailDatesHelpers.js";
import {
  isVacatedLease,
  isOutboundSublease,
  assignVacatedPosture,
} from "./reduceExposure.js";

// ─── Vacated lease helpers ────────────────────────────────────────────────────
//
// A lease is "vacated" when the office stage is Vacated or the lease bucket is
// Vacant-Held (set explicitly on the lease row).  These leases are pulled out
// of the renew/exit decision bands before any bucket logic runs and placed into
// two posture bands: "sublease" or "winding-down".

export { isVacatedLease, isOutboundSublease, assignVacatedPosture } from "./reduceExposure.js";

// ─── Constants ────────────────────────────────────────────────────────────────

// Ordered display list (highest priority first).
export const ACTION_BUCKETS = [
  { id: "decide-now", label: "Decide now", rangeLabel: "0-90 days",   meaning: "Imminent decision window" },
  { id: "negotiate",  label: "Negotiate",  rangeLabel: "3-12 months", meaning: "Engage landlord now"      },
  { id: "reduce-exposure", label: "Reduce exposure", rangeLabel: "Vacant posture", meaning: "Offset or exit carry cost" },
  { id: "rolling-mtm", label: "Rolling / MTM", rangeLabel: "No fixed expiry", meaning: "Active lease has rolled beyond its stated expiry" },
  { id: "monitor",    label: "Monitor",    rangeLabel: "12+ months", meaning: "Watch and revisit later"   },
];

// Focus window label → days (Infinity = no limit).
export const FOCUS_WINDOW_DAYS = {
  "90d":  90,
  "12mo": 365,
  "24mo": 730,
  "all":  Infinity,
};

// Horizon label → days from today (Infinity = derived from lease end dates).
export const HORIZON_DAYS = {
  "3yr":  3 * 365,
  "5yr":  5 * 365,
  "full": Infinity,
};

// Critical date types that represent a real decision point.
// Exported (MARKET-PLAN-LEADERSHIP-VIEW-S1) so other surfaces — e.g. the
// Market Plan table's "Next decision" column — can reuse the exact same
// decision-point vocabulary instead of re-deriving it.
export const ACTION_DATE_TYPES = new Set([
  "renewal_notice",
  "renewal_option",
  "break_option",
  "break_notice",
  "notice_deadline",
  "rolling_review",
]);

function currentTermActionMarkers(lease, criticalDates, today) {
  const markers = getUpcomingMarkers(lease, criticalDates);
  const selection = selectCurrentTermKeyDates(criticalDates, lease, today);
  const expiredLeaseRecovery = lease?.status === "expired"
    && selection.expiryKey
    && selection.expiryKey < selection.todayKey;
  const actionableDates = expiredLeaseRecovery
    ? [...selection.eligibleDates, ...selection.outsideExpiryDates]
    : selection.eligibleDates;
  const eligibleIds = new Set(actionableDates.map((date) => date.id));
  return markers.filter((marker) =>
    !CURRENT_TERM_KEY_DATE_TYPES.has(marker.type) || eligibleIds.has(marker.id),
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function assignBucket(days) {
  if (days === null || days > 365)  return "monitor";
  if (days <= 90)                   return "decide-now";
  if (days <= 365)                  return "negotiate";
  return "monitor";
}

// The list-view Health/Outlook vocabulary retains its legacy Model tier.
// The canonical action selector above intentionally does not expose that
// presentation-only bucket.
function assignHealthBucket(days) {
  if (days === null || days > 730) return "monitor";
  if (days <= 90) return "decide-now";
  if (days <= 365) return "negotiate";
  return "model";
}

// Apply the Focus override after the initial bucket assignment.
function applyFocusOverride(bucket, days, focusDays) {
  if (bucket === "monitor") return "monitor";
  if (focusDays !== Infinity && days !== null && days > focusDays) return "monitor";
  return bucket;
}

function shortReasonFor(nextActionType, bucket) {
  if (nextActionType === "break_notice")    return "Break notice";
  if (nextActionType === "break_option")    return "Break option";
  if (nextActionType === "renewal_notice")  return "Renewal notice";
  if (nextActionType === "renewal_option")  return "Renewal option";
  if (nextActionType === "notice_deadline") return "Notice deadline";
  if (nextActionType === "rolling_review")  return "Rolling review";
  // Fallback = expiration date was used
  if (bucket === "decide-now") return "Lease expiration";
  if (bucket === "negotiate")  return "Lease expiration";
  return "Monitoring";
}

function dateKey(value) {
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) return null;
    if (
      value.getUTCHours() === 0
      && value.getUTCMinutes() === 0
      && value.getUTCSeconds() === 0
    ) return value.toISOString().slice(0, 10);
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  const match = String(value ?? "").match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function realExpiryDate(lease) {
  if (!isSentinelEndDate(lease?.endDate)) return dateKey(lease?.endDate);
  return dateKey(lease?.soonestPossibleExpiration);
}

function isRollingTerm(lease) {
  const term = String(lease?.termType ?? lease?.term_type ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");
  return ["rolling", "mtm", "monthtomonth", "rollingmtm"].includes(term);
}

function hasFutureEarliestTermination(lease, todayKey) {
  const earliest = dateKey(lease?.earliestTermination ?? lease?.earliest_termination);
  return !!earliest && !!todayKey && earliest > todayKey;
}

function isBreakOptionNoticeDate(criticalDates, dateValue) {
  const targetKey = dateKey(dateValue);
  return !!targetKey && (criticalDates || []).some((date) => {
    const category = String(date.categoryRaw ?? date.category_raw ?? date.label ?? "");
    return date.type === "break_option"
      && dateKey(date.dueDate) === targetKey
      && /\bnotice\b/i.test(category);
  });
}

function dateDaysFromToday(dateValue, today) {
  const target = parseDateOnly(dateValue);
  const start = parseDateOnly(today);
  return target && start
    ? Math.round((target.getTime() - start.getTime()) / (24 * 60 * 60 * 1000))
    : null;
}

function boundedNoticeEndKeys(criticalDates, lease, today) {
  const { eligibleDates = [] } = selectCurrentTermKeyDates(criticalDates, lease, today);
  const boundaries = eligibleDates.map((date) => {
    const label = String(date.categoryRaw ?? date.category_raw ?? date.label ?? "").trim();
    const match = label.match(/^(.*\bnotice period)\s+(starts|ends)$/i);
    return match
      ? { date, name: match[1].toLowerCase().replace(/\s+/g, " "), side: match[2].toLowerCase() }
      : null;
  }).filter(Boolean);
  const expiredEnds = new Set();

  for (const end of boundaries.filter((item) => item.side === "ends")) {
    const endKey = dateKey(end.date.dueDate);
    if (!endKey || endKey >= dateKey(today)) continue;
    const endAreas = String(end.date.areas ?? "").trim().toLowerCase();
    const hasMatchingStart = boundaries.some((start) => {
      if (
        start.side !== "starts"
        || start.name !== end.name
        || start.date.type !== end.date.type
        || dateKey(start.date.dueDate) > endKey
      ) return false;
      const startAreas = String(start.date.areas ?? "").trim().toLowerCase();
      return !startAreas || !endAreas || startAreas === endAreas;
    });
    if (hasMatchingStart) expiredEnds.add(endKey);
  }

  return expiredEnds;
}

const NOTICE_ACTION_TYPES = new Set(["renewal_notice", "notice_deadline", "break_notice"]);
const VERIFIED_DECISION_DATE_TYPES = new Set([
  "renewal_notice",
  "renewal_option",
  "break_option",
  "break_notice",
  "notice_deadline",
]);

/**
 * Pick one canonical action date for the timeline.
 *
 * Explicit current-term critical dates remain eligible alongside normalized
 * lease fields. A past notice is retained as the open-window signal only while
 * the lease expiry is still ahead; otherwise expiry is the terminal fallback.
 */
export function deriveLeaseActBy(lease, criticalDates = [], today = new Date()) {
  const todayKey = dateKey(today);
  const expiryKey = realExpiryDate(lease);
  const eligibleCriticalDates = (criticalDates || []).filter((date) =>
    !VERIFIED_DECISION_DATE_TYPES.has(date.type)
    || isVerifiedLeaseDecisionDate(date),
  );
  const currentTermKey = dateKey(getCurrentTermStartDate(eligibleCriticalDates, lease));
  const markers = currentTermActionMarkers(lease, eligibleCriticalDates, today);
  const boundedEnds = boundedNoticeEndKeys(eligibleCriticalDates, lease, today);
  const candidates = [];
  const addCandidate = (
    dateValue,
    type,
    noticePeriodUnknown = false,
    state = null,
    fromBoundedWindow = false,
  ) => {
    const key = dateKey(dateValue);
    if (!key || !type || (currentTermKey && key < currentTermKey)) return;
    if (boundedEnds.has(key) && !fromBoundedWindow) return;
    if (expiryKey && key >= expiryKey) return;
    // Notice durations are not dates. A notice deadline is actionable only
    // when its sourced date is within 90 days, or it belongs to an explicitly
    // open current-term notice window.
    const daysUntil = dateDaysFromToday(key, today);
    if (
      NOTICE_ACTION_TYPES.has(type)
      && (daysUntil === null
        || (daysUntil > 90 && state !== "notice-open")
        || (daysUntil < 0 && state !== "notice-open"))
    ) return;
    const candidate = { date: key, type, noticePeriodUnknown, state };
    const existingIndex = candidates.findIndex((item) => item.date === key);
    if (existingIndex === -1) {
      candidates.push(candidate);
      return;
    }
    const existing = candidates[existingIndex];
    const rank = (item) => (item.state ? 100 : 0)
      + (item.type === "break_notice" ? 10 : 0)
      + (item.noticePeriodUnknown ? 0 : 1);
    if (rank(candidate) > rank(existing)) candidates[existingIndex] = candidate;
  };

  // Reuse the canonical notice-window pairing rules. A paired "Starts"/"Ends"
  // window acts by its closing date; once that end passes, the bounded window
  // is no longer eligible. One-date notices retain their open-until-expiry
  // behavior.
  const noticeWindowTypes = [
    "renewal_notice",
    "notice_deadline",
    "break_notice",
    "break_option",
  ];
  for (const type of noticeWindowTypes) {
    const window = selectCurrentTermNoticeWindow(
      eligibleCriticalDates,
      lease,
      today,
      new Set([type]),
    );
    if (!window.noticeDt || window.state === "no-notice") continue;
    const bounded = !!window.periodEndDateValue;
    const deadline = window.periodEndDateValue || window.noticeDateValue;
    // Ordinary break-option dates are converted below from the normalized
    // break date and its known lead (or are flagged as unknown).
    if (type === "break_option" && !bounded) {
      if (isBreakOptionNoticeDate(eligibleCriticalDates, window.noticeDateValue)) {
        addCandidate(deadline, "break_notice", false, window.state);
      }
      continue;
    }
    const actionType = type === "break_option" && bounded
      ? "break_notice"
      : type;
    addCandidate(deadline, actionType, false, window.state, bounded);
  }

  // Other current-term markers remain direct action dates. Notice-window
  // types are handled above so "Starts" rows cannot outrank their paired end.
  for (const marker of markers || []) {
    if (!marker?.dueDate) continue;
    if (marker.type === "renewal_notice" || marker.type === "notice_deadline"
      || marker.type === "break_notice" || marker.type === "break_option") continue;
    if (marker.type === "renewal_option" || marker.type === "rolling_review") {
      addCandidate(marker.dueDate, marker.type);
    }
  }

  // The normalized next-notice field is Spacebase's sourced deadline. Its
  // source text is not parsed or used to invent a date.
  if (lease?.nextNoticeDeadline && isVerifiedLeaseDecisionDate(lease, "nextNoticeDeadline")) {
    addCandidate(lease.nextNoticeDeadline, "notice_deadline");
  }

  const breakDates = new Set();
  const leaseBreakKey = isVerifiedLeaseDecisionDate(lease, "breakDate")
    ? dateKey(lease?.breakDate)
    : null;
  if (leaseBreakKey) breakDates.add(leaseBreakKey);
  for (const marker of markers || []) {
    if (marker?.type === "break_option" && dateKey(marker.dueDate)) {
      breakDates.add(dateKey(marker.dueDate));
    }
  }

  const breakNoticeMonths = lease?.breakNoticeMonths == null
    ? null
    : Number(lease.breakNoticeMonths);
  const breakNoticeLeadKnown = Number.isInteger(breakNoticeMonths) && breakNoticeMonths >= 0;
  for (const breakDate of breakDates) {
    // Use the populated break date itself. breakNoticeMonths describes a
    // duration, not a sourced notice deadline, so it cannot manufacture one.
    addCandidate(breakDate, "break_option", !breakNoticeLeadKnown);
  }

  const future = candidates
    .filter((candidate) => !todayKey || candidate.date >= todayKey)
    .sort((a, b) => a.date.localeCompare(b.date) || a.type.localeCompare(b.type));
  // If every notice deadline has passed but expiry is still ahead, retain the
  // most recent missed deadline so the UI can show the notice-open state.
  const elapsedNoticeTypes = new Set(["renewal_notice", "notice_deadline", "break_notice"]);
  const elapsed = candidates
    .filter((candidate) => todayKey && candidate.date < todayKey && elapsedNoticeTypes.has(candidate.type))
    .sort((a, b) => b.date.localeCompare(a.date) || a.type.localeCompare(b.type));
  const selectedNotice = future[0] || (expiryKey && expiryKey > todayKey ? elapsed[0] : null);

  if (selectedNotice) {
    const expiryIsAhead = !expiryKey || expiryKey > todayKey;
    return {
      actByDate: selectedNotice.date,
      actByType: selectedNotice.type,
      noticePeriodUnknown: selectedNotice.noticePeriodUnknown,
      daysToActBy: dateDaysFromToday(selectedNotice.date, today),
      actByState: selectedNotice.state || (selectedNotice.date < todayKey && expiryIsAhead
        ? "notice-open"
        : selectedNotice.date < todayKey
          ? "expired"
          : "notice-future"),
    };
  }

  return {
    actByDate: expiryKey,
    actByType: expiryKey ? "expiry" : null,
    noticePeriodUnknown: false,
    daysToActBy: expiryKey ? dateDaysFromToday(expiryKey, today) : null,
    actByState: expiryKey && expiryKey <= todayKey ? "expired" : "no-notice",
  };
}

// ─── Main selector ────────────────────────────────────────────────────────────

/**
 * @param {Array}  leases        filteredLeases from LeasesPage (src/v2/leases/LeasesPage.jsx)
 * @param {Array}  criticalDates CriticalDateRow[] from getCriticalDates
 * @param {Date}   today
 * @param {number} focusDays     from FOCUS_WINDOW_DAYS[focus]; default 730
 * @returns {Array} enriched lease objects
 */
export function deriveLeaseActions(leases, criticalDates, today, focusDays = 730) {
  // Index criticalDates by leaseId for O(1) lookup.
  const cdByLease = {};
  for (const cd of criticalDates || []) {
    if (!cd.leaseId) continue;
    if (!cdByLease[cd.leaseId]) cdByLease[cd.leaseId] = [];
    cdByLease[cd.leaseId].push(cd);
  }

  return (leases || []).map((lease) => {
    const leaseDates = cdByLease[lease.id] || [];
    const markers = currentTermActionMarkers(lease, leaseDates, today);
    const actBy = deriveLeaseActBy(lease, leaseDates, today);

    // ── Vacated lease early-return ───────────────────────────────────────────
    // Vacated leases and outbound subleases are NOT renew/exit decisions —
    // they have a known exposure posture. Pull them out before the action-date
    // / bucket logic so they never appear in Decide now, Negotiate, or Model
    // bands. Outbound subleases can still be operational while they are being
    // marketed, so their lease type/status is an independent trigger.
    // Draft leases have no term yet — draft takes priority over vacated stage,
    // mirroring the classifyLeaseHealth guard order. A draft lease in a vacated
    // office should fall through to normal bucket logic, not receive a vacated posture.
    if (
      (isVacatedLease(lease) || isOutboundSublease(lease))
      && lease.status !== "expired"
      && lease.status !== "draft"
    ) {
      const posture = assignVacatedPosture(lease);
      const anchor = resolveTimelineEndAnchor(lease);
      const expiryDate = anchor.date ? parseDateOnly(anchor.date) : null;
      const carryMs = expiryDate ? Math.max(0, expiryDate - today) : null;
      // carryMonths: months from today to expiry (null when no expiry date)
      const carryMonths = carryMs !== null
        ? Math.round(carryMs / (1000 * 60 * 60 * 24 * 30.4375))
        : null;
      // carryAtRiskUsd: annualRentUsd × carryMonths / 12 (null when cost unknown)
      const carryAtRiskUsd =
        carryMonths !== null && lease.annualRentUsd != null
          ? lease.annualRentUsd * carryMonths / 12
          : null;
      return {
        ...lease,
        ...actBy,
        next_action_date:     actBy.actByDate,
        next_action_type:     actBy.actByType === "expiry" ? null : actBy.actByType,
        days_to_next_action:  actBy.daysToActBy,
        action_window_bucket: "reduce-exposure",
        posture,
        carryMonths,
        carryAtRiskUsd,
        action_value:         lease.annualRentUsd != null ? Number(lease.annualRentUsd) : 0,
        action_short_reason:  posture === "sublease" ? "Sublease" : "Winding-down",
        isStrategic:          false,
      };
    }

    // Preserve the existing holdover/expired split based on the timeline
    // endpoint, while all action urgency now uses the canonical act-by date.
    const anchor = resolveTimelineEndAnchor(lease);
    const anchorDate = anchor.date ? parseDateOnly(anchor.date) : null;
    const hasPastAnchor = !!anchorDate && anchorDate <= today;
    const expiryKey = realExpiryDate(lease);
    const hasPastExpiry = !!expiryKey && expiryKey < dateKey(today);
    const isActive = lease.status === "active";
    const isRollingMtm = isActive && hasPastExpiry
      && (isRollingTerm(lease) || hasFutureEarliestTermination(lease, dateKey(today)));

    if (hasPastExpiry) {
      return {
        ...lease,
        ...actBy,
        next_action_date:     null,
        next_action_type:     null,
        days_to_next_action:  null,
        action_window_bucket: isRollingMtm ? "rolling-mtm" : "expired",
        posture:              isRollingMtm ? "rolling-mtm" : "expired",
        action_value:         lease.annualRentUsd != null
          ? Number(lease.annualRentUsd)
          : (lease.totalCost != null ? Number(lease.totalCost) : 0),
        action_short_reason:  isRollingMtm ? "Rolling / MTM" : "Expired",
        isStrategic:          false,
      };
    }

    const isPastExpiryFallback = actBy.actByType === "expiry" && hasPastAnchor;
    const days = isPastExpiryFallback ? null : actBy.daysToActBy;
    const nextActionDate = actBy.actByDate;
    const nextActionType = actBy.actByType === "expiry" ? null : actBy.actByType;

    // LEASES-ACTIVE-OFFICE-EXPIRED-OUTLOOK-P1:
    // Non-vacated leases with no future date that are either expired-status OR
    // have a past anchor are in holdover or expired. This catches:
    //   (a) status='expired' with no future dates (AMS-02 production pattern)
    //   (b) status='active' but end_date in the past (SYD-05 pattern)
    // Rolling leases (sentinel endDate, no SPE/NND) produce days=null but
    // hasPastAnchor=false and status='active' — they stay as "monitor".
    //
    // LEASES-TIMELINE-CLEANUP: split holdover vs expired by locationStage.
    //   - operational or dormant → genuine holdover (office still in active use
    //     past its contractual end; needs attention)
    //   - closed or other non-active stage → expired (office is done; no action)
    if (days === null && (lease.status === "expired" || hasPastAnchor)) {
      const isActiveStage = lease.locationStage === "operational" || lease.locationStage === "dormant";
      const bucket = isActiveStage ? "decide-now" : "expired";
      return {
        ...lease,
        ...actBy,
        next_action_date:     null,
        next_action_type:     null,
        days_to_next_action:  null,
        action_window_bucket: bucket,
        posture:              isActiveStage ? "holdover" : "expired",
        action_value:         lease.annualRentUsd != null
          ? Number(lease.annualRentUsd)
          : (lease.totalCost != null ? Number(lease.totalCost) : 0),
        action_short_reason:  isActiveStage ? "Holdover" : "Expired",
        isStrategic:          false,
      };
    }

    // Bucket assignment + Focus override.
    const rawBucket = assignBucket(days);
    const bucket    = applyFocusOverride(rawBucket, days, focusDays);

    return {
      ...lease,
      ...actBy,
      next_action_date:     nextActionDate,
      next_action_type:     nextActionType,
      days_to_next_action:  days,
      action_window_bucket: bucket,
      action_value:         lease.annualRentUsd != null
        ? Number(lease.annualRentUsd)
        : (lease.totalCost != null ? Number(lease.totalCost) : 0),
      action_short_reason:  actBy.actByType === "expiry"
        ? "Lease expiry"
        : shortReasonFor(nextActionType, bucket),
      isStrategic:          false,
    };
  });
}

// ── Bucket → health display map ───────────────────────────────────────────────

// L-34: Per-descriptor sort ordinal used by both legacy (LeasesListTable) and
// v2 (LeasesPage) sort paths. Lower = more urgent; ASC sort puts urgent first.
// Each amber-tier bucket gets its own ordinal so amber leases never interleave.
// L-38: "draft" added at ordinal 0 (first in ascending sort); all existing
// buckets shifted up by 1. Unknown / null buckets resolve to 9 at call sites via ?? 9.
export const OUTLOOK_DESCRIPTOR_ORDER = {
  "draft":        0,
  "decide-now":   1,
  "holdover":     2,
  "negotiate":    3,
  "model":        4,
  "sublease":     5,
  "monitor":      6,
  "winding-down": 7,
  "expired":      8,
};

const HEALTH_BUCKET_DISPLAY = {
  "decide-now":   { word: "Decide",    colorTier: "red"   },
  "negotiate":    { word: "Negotiate", colorTier: "amber" },
  "model":        { word: "Model",     colorTier: "amber" },
  "sublease":     { word: "Sublease",  colorTier: "amber" },
  "monitor":      { word: "Secure",    colorTier: "green" },
  "winding-down": { word: "Wind-Down", colorTier: "slate" },
  // LEASES-ACTIVE-OFFICE-EXPIRED-OUTLOOK-P1: In-Use leases whose anchor is in
  // the past and have no future critical dates are in holdover (not Unknown).
  "holdover":     { word: "Holdover",  colorTier: "amber" },
  // LEASES-TIMELINE-CLEANUP: leases on closed/non-active offices with a past
  // anchor — the office is done, no renewal action needed.
  "expired":      { word: "Expired",   colorTier: "grey" },
};

/**
 * _isPastAnchorHoldover(lease) — shared gate for classifyLeaseHealth.
 * Returns true when the lease's office is still in active use (operational
 * or dormant), meaning a past anchor represents a genuine holdover situation.
 * Returns false for closed or unset location stages — those should read
 * "Expired" (slate) since no occupancy action is pending.
 *
 * This gate belongs to List Health/Outlook only; the timeline uses expiry
 * and explicit rolling-term evidence to place its rows.
 */
function _isPastAnchorHoldover(lease) {
  return lease.locationStage === "operational" || lease.locationStage === "dormant";
}

/**
 * classifyLeaseHealth(lease, criticalDates, today?)
 *
 * Independent List Health/Outlook classification for a single lease,
 * returning { bucket, word, colorTier } for list-view display.
 * Uses Infinity for focusDays (no focus override in the list view).
 *
 * colorTier: "red" | "amber" | "green" | "slate" | "grey"
 */
export function classifyLeaseHealth(lease, criticalDates = [], today = new Date()) {
  if (!lease) return { bucket: null, word: "Unknown", colorTier: "grey" };

  // Draft leases have no term yet — always show Draft regardless of dates.
  if (lease.status === "draft") {
    return { bucket: "draft", word: "Draft", colorTier: "blue" };
  }

  // Vacated leases (Vacated stage or Vacant-Held lease status) have a known
  // posture independent of expiry — unless the lease is already expired, in
  // which case the expired-status check below takes precedence.
  if (isVacatedLease(lease) && lease.status !== "expired") {
    const bucket = assignVacatedPosture(lease);
    const display = HEALTH_BUCKET_DISPLAY[bucket] ?? { word: "Unknown", colorTier: "grey" };
    return { bucket, ...display };
  }

  // LEASES-ACTIVE-OFFICE-EXPIRED-OUTLOOK-P1:
  // For non-vacated (In-Use) leases, do NOT short-circuit on status='expired'.
  // An In-Use office whose lease is marked expired may still have future
  // critical dates (e.g. a renewal_notice) that define the correct outlook.
  // We fall through to the marker + anchor logic below and classify from dates.
  // Only vacated leases propagate the expired status to the display bucket.

  const cdByLease = {};
  for (const cd of criticalDates) {
    if (!cd.leaseId) continue;
    if (!cdByLease[cd.leaseId]) cdByLease[cd.leaseId] = [];
    cdByLease[cd.leaseId].push(cd);
  }
  const markers = currentTermActionMarkers(lease, cdByLease[lease.id] || [], today);

  let nextActionDate = null;
  for (const m of markers) {
    if (!ACTION_DATE_TYPES.has(m.type)) continue;
    if (!m.dueDate) continue;
    const d = parseDateOnly(m.dueDate);
    if (!d || d <= today) continue;
    if (!nextActionDate || d < parseDateOnly(nextActionDate)) {
      nextActionDate = m.dueDate;
    }
  }

  if (!nextActionDate) {
    const anchor = resolveTimelineEndAnchor(lease);
    if (anchor.date) {
      const anchorDate = parseDateOnly(anchor.date);
      if (anchorDate && anchorDate > today) {
        nextActionDate = anchor.date;
      } else if (anchorDate) {
        // LEASES-ACTIVE-OFFICE-EXPIRED-OUTLOOK-P1 / LEASES-TIMELINE-CLEANUP:
        // Anchor exists but is in the past. Split by locationStage:
        //   operational/dormant → genuine holdover (office still active past expiry)
        //   closed or other     → expired (office is done; lease data needs clean-up)
        const pastBucket = _isPastAnchorHoldover(lease) ? "holdover" : "expired";
        const pastDisplay = HEALTH_BUCKET_DISPLAY[pastBucket];
        return { bucket: pastBucket, ...pastDisplay, dataGap: true };
      }
    }
  }

  if (!nextActionDate) {
    // LEASES-ACTIVE-OFFICE-EXPIRED-OUTLOOK-P1 / LEASES-TIMELINE-CLEANUP:
    // Expired-status leases with no anchor at all — same locationStage split.
    // True rolling leases (sentinel end_date, no SPE/NND, no critical dates)
    // remain Unknown — they have no contractual cliff.
    if (lease.status === "expired") {
      const pastBucket = _isPastAnchorHoldover(lease) ? "holdover" : "expired";
      const pastDisplay = HEALTH_BUCKET_DISPLAY[pastBucket];
      return { bucket: pastBucket, ...pastDisplay, dataGap: true };
    }
    return { bucket: null, word: "Unknown", colorTier: "grey" };
  }

  const nextDateObj = parseDateOnly(nextActionDate);
  const days = nextDateObj
    ? Math.round((nextDateObj - today) / (1000 * 60 * 60 * 24))
    : null;

  const bucket = assignHealthBucket(days);
  const display = HEALTH_BUCKET_DISPLAY[bucket] ?? { word: "Unknown", colorTier: "grey" };
  return { bucket, ...display };
}

/**
 * selectPriorityLease — finds the lease to pre-select in the Insight panel.
 * Returns the lease with the smallest days_to_next_action in the highest-
 * priority non-empty bucket (decide-now → negotiate → model → monitor).
 *
 * @param {Array} derivedLeases — output of deriveLeaseActions
 * @returns {Object|null}
 */
export function selectPriorityLease(derivedLeases) {
  const PRIORITY_ORDER = ["decide-now", "negotiate", "reduce-exposure", "monitor"];
  for (const bucket of PRIORITY_ORDER) {
    const inBucket = (derivedLeases || []).filter(
      (l) => l.action_window_bucket === bucket,
    );
    if (inBucket.length === 0) continue;
    // Smallest days_to_next_action first; nulls last.
    return inBucket.slice().sort((a, b) => {
      if (a.days_to_next_action === null) return 1;
      if (b.days_to_next_action === null) return -1;
      return a.days_to_next_action - b.days_to_next_action;
    })[0];
  }
  return null;
}
